import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Receipt as ReceiptIcon,
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  FileUpload as FileUploadIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  LockOutlined as LockOutlinedIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import {
  useProductosMaterialContext,
  guardarFacturaBackend,
  enviarPayloadFacturaElectronica,
  emitirEcfDgii,
  revertirFacturaRechazada,
} from 'context';
import {
  tienePermisosModulo,
  puedeEditarProgramaFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { reglaDescuentoAplicaAProducto } from 'utils/reglasDescuentos';
import ConsultaProducto from 'productosnoserializados/ConsultaProducto';
import VistaFactura from '../facturapos/VistaFactura';
import tiposDocumentosJson from 'utils/tiposDocumentos.json';
import { hoyLocal } from 'lib/fechaLocal';

const PROGRAMA_FACTURA_CREDITO = 'facturacredito';
const TIPO_FACTURA_CREDITO = 'FACTURA CREDITO';
const TIPO_FACTURA_CONTADO = 'FACTURA CONTADO';
const TIPO_DOCUMENTO_MOTOR_VENTAS_CREDITO = 'Ventas a Credito';
const DOCUMENTO_DIARIO_VENTAS_CREDITO = 'FACTURA CREDITO';

const moduloContabilidadInstalado = (modulos) => {
  if (modulos == null || modulos === false || modulos === 'false') return false;
  const partes = Array.isArray(modulos) ? modulos : String(modulos).split(/[,;]/);
  return partes.some((m) => String(m || '').trim().toLowerCase() === 'contabilidad');
};

const enviarMotorContableActivo = (valor) => String(valor || '').trim().toUpperCase() === 'S';

const debeEnviarAlMotor = (modulos, enviarMotorContable) =>
  moduloContabilidadInstalado(modulos) && enviarMotorContableActivo(enviarMotorContable);

const bloqueMotorDeTipo = (tipooperacion) => {
  const clave = String(tipooperacion || '').toLowerCase().replace(/\s/g, '');
  if (!clave) return null;
  return tiposDocumentosJson.find((e) => e?.[clave])?.[clave] || null;
};

const origenFormaPagoReglas = (tipooperacion) => {
  const bloque = bloqueMotorDeTipo(tipooperacion);
  if (!bloque) return [];
  const raw = bloque.origenformapago ?? bloque.origenformadepago ?? [];
  return (Array.isArray(raw) ? raw : [raw]).map((x) => String(x || '').trim()).filter(Boolean);
};

const tipoTieneOrigenFormaPago = (tipooperacion) => origenFormaPagoReglas(tipooperacion).length > 0;

const payloadFormaPagoSiAplica = (tipoDocumento, documentoFormaPago) => {
  const tipo = String(tipoDocumento || '').trim();
  const documento = String(documentoFormaPago || '').trim();
  if (!tipo || !tipoTieneOrigenFormaPago(tipo) || !documento) return {};
  return { forma_pago: documento };
};

const codigoUsaOrigenFormaPago = (tipooperacion, codigoRegla) => {
  const codigo = String(codigoRegla || '').trim().toLowerCase();
  if (!codigo) return false;
  return origenFormaPagoReglas(tipooperacion).some((c) => c.toLowerCase() === codigo);
};

const normalizarTipoMotor = (valor) =>
  String(valor || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');

const documentoMotorDefinido = (registros, tipoDocumento) => {
  const tipo = String(tipoDocumento || '').trim();
  if (!tipo) return false;
  const tipoNorm = normalizarTipoMotor(tipo);
  return (registros || []).some((r) => {
    if (normalizarTipoMotor(r?.tipo_documento) !== tipoNorm) return false;
    if (codigoUsaOrigenFormaPago(tipo, r?.codigo_regla)) return true;
    const aplica = String(r?.aplica_a || 'cuenta').trim().toLowerCase();
    if (aplica === 'json') return true;
    return Boolean(String(r?.cuentacontable || '').trim());
  });
};

const reglasMotorDeDocumento = (registros, tipoDocumento) => {
  const tipoNorm = normalizarTipoMotor(tipoDocumento);
  return (registros || []).filter((r) => normalizarTipoMotor(r?.tipo_documento) === tipoNorm);
};

const CLAVES_META_MOTOR = new Set([
  'tipodocumento',
  'nfactura',
  'documento',
  'numero',
  'suplidor',
  'fecha',
  'tasa',
  'usuario',
  'concepto',
  'valor',
  'valor_total',
  'lineas',
  'cuentas',
]);

const redondearDinero2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const construirDocumentoMotorVenta = (tipoDoc, reglas, ctx) => {
  const lineasFp = Array.isArray(ctx.lineas)
    ? ctx.lineas.filter((ln) => ln?.cuenta && (ln.monto || 0) > 0)
    : [];
  const valor = redondearDinero2(ctx.valorTotal);
  const montos = ctx.montos || {};
  const doc = {
    tipodocumento: tipoDoc,
    nfactura: ctx.nfactura,
    documento: ctx.documento || DOCUMENTO_DIARIO_VENTAS_CREDITO,
    fecha: ctx.fecha,
    tasa: ctx.tasa,
    usuario: ctx.usuario,
    concepto: ctx.concepto,
    valor,
    lineas: lineasFp,
    permitir_descuadre: true,
  };
  if (ctx.numero != null && ctx.numero !== '') {
    doc.numero = ctx.numero;
  }
  (reglas || []).forEach((regla) => {
    const codigo = String(regla?.codigo_regla || '').trim();
    if (!codigo || CLAVES_META_MOTOR.has(codigo.toLowerCase())) return;
    const usaFp =
      codigoUsaOrigenFormaPago(tipoDoc, codigo) ||
      String(regla?.aplica_a || '').trim().toLowerCase() === 'json';
    if (usaFp) {
      doc[codigo] = lineasFp;
      return;
    }
    const extra = montos[codigo] ?? montos[codigo.toLowerCase()];
    doc[codigo] = redondearDinero2(extra != null ? extra : valor);
  });
  return doc;
};

/** Códigos e-CF permitidos en Factura Crédito (sufijo del nombre, p.ej. "CREDITO FISCAL:E31" → 31). */
const CODIGOS_DOCUMENTO_CREDITO = ['31', '32', '44', '45','99'];

/** Extrae el código numérico del tipo fiscal (campo codigo o sufijo tras ":" en nombre). */
const codigoTipoDocumento = (tipo) => {
  const desdeCodigo = String(tipo?.codigo || '').replace(/\D/g, '');
  if (desdeCodigo) return desdeCodigo;
  return (tipo?.nombre || '').split(':')[1]?.replace(/\D/g, '') || '';
};

const fmtMoney = (v) =>
  (parseFloat(v) || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const filterHeaderSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.74rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  bgcolor: FP.color.surfaceAlt,
  borderBottom: `1px solid ${FP.color.border}`,
  py: 1.2,
};

/**
 * Factura a crédito: captura ítems, calcula descuentos por regla
 * (línea y general), tasa impuestos en backend y guarda el documento. Permite
 * importar una cotización previa. Seguridad: programa `facturacredito`.
 */
const FacturaCredito = () => {
  const { controller, selectedCompany, showSuccess, showError, showWarning } = useProductosMaterialContext();
  const {
    nombreNegocio,
    usuarioLog,
    claveLog,
    roleDetalles,
    comprobanteelectronico,
    rnc_empresa,
    direccion_empresa,
    modulos,
    enviarMotorContable,
  } = controller;

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const permisoLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_FACTURA_CREDITO),
    [roleDetalles]
  );
  const permisoModificar = useMemo(
    () => puedeEditarProgramaFacturacion(roleDetalles, PROGRAMA_FACTURA_CREDITO),
    [roleDetalles]
  );

  const [productos, setProductos] = useState([]);
  const [, setProductosOriginales] = useState([]);

  const [reglasImpuestos, setReglasImpuestos] = useState([]);
  const [reglasDescuentos, setReglasDescuentos] = useState([]);
  const [reglasDescuentosGenerales, setReglasDescuentosGenerales] = useState([]);
  const [tiposDocumentosFiscales, setTiposDocumentosFiscales] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [listasPrecios, setListasPrecios] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const [tipoFactura, setTipoFactura] = useState('credito');
  const [listaPrecioCliente, setListaPrecioCliente] = useState(null);
  // 'detalle' | 'mayor': pasa a 'mayor' solo cuando el cliente tiene lista; el
  // usuario puede cambiarlo a mano en cualquier momento.
  const [tipoPrecio, setTipoPrecio] = useState('detalle');
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [configCliente, setConfigCliente] = useState(null);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [itemsFactura, setItemsFactura] = useState([]);
  const [impuestosFactura, setImpuestosFactura] = useState([]);
  const [tipoDocumento, setTipoDocumento] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [busquedaProducto, setBusquedaProducto] = useState('');
  const [dialogoProductoAbierto, setDialogoProductoAbierto] = useState(false);
  const [dialogoConsultaProducto, setDialogoConsultaProducto] = useState(false);
  const [cantidadAgregar, setCantidadAgregar] = useState(1);
  const [cantidadesPorProducto, setCantidadesPorProducto] = useState({});

  const sucursalActual = useMemo(
    () => localStorage.getItem('sucursal') || '',
    []
  );

  const getCantidadProducto = (codigo) =>
    cantidadesPorProducto[codigo] ?? 1;

  const setCantidadProducto = (codigo, valor) => {
    const num = Math.max(0.01, parseFloat(valor) || 1);
    setCantidadesPorProducto((prev) => ({ ...prev, [codigo]: num }));
  };

  const [vistaFacturaAbierta, setVistaFacturaAbierta] = useState(false);
  const [datosVistaFactura, setDatosVistaFactura] = useState({});

  const [dialogoCotizaciones, setDialogoCotizaciones] = useState(false);
  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargandoCotizaciones, setCargandoCotizaciones] = useState(false);
  const [busquedaCotizacion, setBusquedaCotizacion] = useState('');
  const [cotizacionSeleccionada, setCotizacionSeleccionada] = useState(null);

  const [dialogoOrdenesPedido, setDialogoOrdenesPedido] = useState(false);
  const [ordenesPedido, setOrdenesPedido] = useState([]);
  const [cargandoOrdenesPedido, setCargandoOrdenesPedido] = useState(false);
  const [busquedaOrdenPedido, setBusquedaOrdenPedido] = useState('');
  const [ordenPedidoSeleccionada, setOrdenPedidoSeleccionada] = useState(null);

  const totalDescuentosLinea = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.descuento) || 0),
    0
  );
  const subtotalAntesDescuentos = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.cantidad) || 0) * (parseFloat(item.precio) || 0),
    0
  );
  const subtotal = itemsFactura.reduce(
    (sum, item) => sum + (parseFloat(item.subtotal) || 0),
    0
  );

  const docFiscalSeleccionado = useMemo(
    () => tiposDocumentosFiscales.find((t) => t.nombre === tipoDocumento) || null,
    [tiposDocumentosFiscales, tipoDocumento]
  );

  const esEspecial = useMemo(() => {
    if (!docFiscalSeleccionado) {
      return String(tipoDocumento || '').toLowerCase().includes('especial');
    }
    const codigo = codigoTipoDocumento(docFiscalSeleccionado);
    const encabezado = (docFiscalSeleccionado.nombre || '').split(':')[1]?.trim().toUpperCase() || '';
    return (
      codigo === '44' ||
      encabezado.startsWith('E44') ||
      String(tipoDocumento || '').toLowerCase().includes('especial')
    );
  }, [docFiscalSeleccionado, tipoDocumento]);

  const esEspecialRef = useRef(esEspecial);
  esEspecialRef.current = esEspecial;

  const cambiarDocumentoFiscal = (nuevoTipo) => {
    if (nuevoTipo === tipoDocumento) return;
    setTipoDocumento(nuevoTipo);
    setItemsFactura([]);
    setImpuestosFactura([]);
  };

  const reglaDescuentoGeneralAplicada = useMemo(() => {
    const reglasDoc = (reglasDescuentosGenerales || []).filter(
      (r) =>
        r.activa === 'S' &&
        (r.tipo_documento === tipoDocumento ||
          r.tipo_documento === TIPO_FACTURA_CREDITO ||
          r.tipo_documento === TIPO_FACTURA_CONTADO)
    );
    const aplicables = reglasDoc.filter(
      (r) =>
        r.aplicar_solo_por_encima_monto !== 'S' ||
        (r.monto_minimo != null && subtotal >= parseFloat(r.monto_minimo))
    );
    if (aplicables.length === 0) return null;
    aplicables.sort(
      (a, b) => (parseFloat(b.monto_minimo) || 0) - (parseFloat(a.monto_minimo) || 0)
    );
    return aplicables[0];
  }, [reglasDescuentosGenerales, tipoDocumento, subtotal]);

  const descuentoGeneralTotal = useMemo(() => {
    if (!reglaDescuentoGeneralAplicada || subtotal <= 0) return 0;
    const pct = parseFloat(reglaDescuentoGeneralAplicada.porcentaje) || 0;
    return subtotal * (pct / 100);
  }, [reglaDescuentoGeneralAplicada, subtotal]);

  const totalDescuentos = totalDescuentosLinea + descuentoGeneralTotal;
  const totalImpuestos = esEspecial
    ? 0
    : impuestosFactura.reduce((sum, imp) => sum + (parseFloat(imp.monto) || 0), 0);
  const total = subtotal - descuentoGeneralTotal + totalImpuestos;

  const getDescuentoGeneralPorItem = (item) => {
    if (descuentoGeneralTotal <= 0 || subtotal <= 0) return 0;
    const itemSub = parseFloat(item.subtotal) || 0;
    if (listaPrecioCliente !== null) {
      return (itemSub / subtotal) * descuentoGeneralTotal;
    }
    return 0;
  };

  // --- Cargas iniciales ---------------------------------------------------

  const cargarListasPrecios = async () => {
    try {
      const response = await fetch(
        servidor + '/api/productosbackend_listarlistasprecios',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setListasPrecios(data.listas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarProductos = async () => {
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_obtenerproductos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            anomesfacturacion: "productosnoserializados",
            lista_precio_id: listaPrecioCliente?.id ?? null,
            tipo_precio: tipoPrecio,
            localidad: localStorage.getItem('sucursal'),
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        let prods = data.productos || [];
        if (tipoPrecio === 'mayor' && listaPrecioCliente && listasPrecios.length > 0) {
          const lista = listasPrecios.find((l) => l.id === listaPrecioCliente.id);
          if (lista) {
            const pct = 1 - (parseFloat(lista.descuento_precio_final) || 0) / 100;
            // El backend ya devolvió el precio `mayor` del producto cuando lo
            // tiene (precio_origen 'mayor'); el % de la lista solo se aplica
            // a los que quedaron con precio de detalle.
            prods = prods.map((p) =>
              p.precio_origen === 'mayor'
                ? p
                : { ...p, precio: (parseFloat(p.precio) || 0) * pct }
            );
          }
        }
        setProductos(prods);
        setProductosOriginales(data.productos || []);
        return prods;
      }
    } catch (e) {
      console.error(e);
    }
    return null;
  };

  // Reprecia las líneas de producto con la lista recién cargada (cambio manual
  // Detalle/Mayor sin perder lo ya digitado).
  const repreciarItems = (lista, setter, calcDescuento) => {
    setter((prev) =>
      prev.map((item) => {
        if (item.tipo !== 'producto') return item;
        const prod = lista.find((p) => p.codigo === item.codigo);
        if (!prod) return item;
        const precio = parseFloat(prod.precio) || 0;
        const cantidad = parseFloat(item.cantidad) || 0;
        const descuento = calcDescuento ? calcDescuento(prod, cantidad) : parseFloat(item.descuento) || 0;
        return { ...item, precio, descuento, subtotal: Math.max(0, cantidad * precio - descuento) };
      })
    );
  };

  const cargarReglasImpuestos = async () => {
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_obtenerreglasimpuestos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReglasImpuestos(data.reglas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarReglasDescuentos = async () => {
    try {
      const response = await fetch(
        servidor + '/api/productosbackend_obtenerreglasdescuentos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tabla_inventario: selectedCompany?.anomesfacturacion,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentos(data.reglas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarReglasDescuentosGenerales = async () => {
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_obtenerreglasdescuentosgenerales',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setReglasDescuentosGenerales(data.reglas || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarTiposDocumentos = async () => {
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_listartiposdocumentos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        const activos = (data.tipos || []).filter(
          (t) =>
            t.activo === 'S' &&
            CODIGOS_DOCUMENTO_CREDITO.includes(codigoTipoDocumento(t))
        );
        setTiposDocumentosFiscales(activos);
      }
    } catch (e) {
      console.error(e);
    }
  };

  /**
   * Obtiene la configuración de plantilla guardada en `configuracion_factura`
   * para un tipo de documento. Como `ConfiguracionFactura` guarda usando los
   * nombres estáticos del JSON `tiposdocumentocliente` (en MAYÚSCULAS) y aquí
   * usamos los nombres dinámicos de `tipos_documentos_fiscales` definidos por
   * el usuario, hacemos varios intentos en cascada: nombre exacto → mismo en
   * mayúsculas → genéricos típicos (`FACTURA`, `FACTURA POS`, `Factura`). Se
   * devuelve la primera coincidencia real (`configuracionGuardada === true`)
   * y, si ninguna existe, la configuración por defecto del backend.
   */
  const obtenerConfiguracionParaTipo = async (tipoDoc) => {
    if (!selectedCompany?.db_empresa) return null;
    const nombreTipoTrim = (tipoDoc || '').trim();
    const intentos = [];
    if (nombreTipoTrim) intentos.push(nombreTipoTrim);
    const upper = nombreTipoTrim.toUpperCase();
    if (upper && !intentos.includes(upper)) intentos.push(upper);
    for (const fallback of ['FACTURA CREDITO', 'FACTURA CONTADO', 'FACTURA POS', 'Factura']) {
      if (!intentos.includes(fallback)) intentos.push(fallback);
    }
    let ultimaRespuesta = null;
    for (const intento of intentos) {
      try {
        const response = await fetch(
          servidor + '/api/facturabackend_obtenerconfiguracion',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              tipo_documento: intento,
            }),
          }
        );
        if (!response.ok) continue;
        const data = await response.json();
        ultimaRespuesta = data?.configuracion || ultimaRespuesta;

        console.log(data,"MANUEL");
        if (data?.configuracionGuardada && data?.configuracion) {
          return data.configuracion;
        }
      } catch (_) {
        // intento siguiente
      }
    }
    return ultimaRespuesta;
  };

  const cargarClientes = async () => {
    try {
      const response = await fetch(servidor + '/api/clientesbackend_buscar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo: 'Cliente',
          descripcion: '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setClientes(data.resultados || data.clientes || []);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const cargarConfigCliente = async (clienteId) => {
    try {
      const response = await fetch(servidor + '/api/clientesbackend_obtenerporid', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          id: clienteId,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setConfigCliente(data.cliente || null);
      } else {
        setConfigCliente(null);
      }
    } catch (e) {
      setConfigCliente(null);
    }
  };

  const cargarDatos = async () => {
    setCargando(true);
    try {
      await Promise.all([
        cargarProductos(),
        cargarReglasImpuestos(),
        cargarReglasDescuentos(),
        cargarReglasDescuentosGenerales(),
        cargarTiposDocumentos(),
        cargarClientes(),
        cargarListasPrecios(),
      ]);
    } catch (e) {
      showError('Error al cargar datos');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (permisoLeer && selectedCompany?.db_empresa) cargarDatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permisoLeer, selectedCompany]);

  const enviarAlMotorContableVenta = useCallback(
    async (tipoDoc, ctxDocumento) => {
      if (!debeEnviarAlMotor(modulos, enviarMotorContable)) return;
      if (!selectedCompany?.db_empresa) return;
      try {
        const reglasRes = await fetch(`${servidor}/contabilidadbackend_listarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            tipodocumento: tipoDoc,
            ...payloadFormaPagoSiAplica(tipoDoc, ctxDocumento.formaPago),
          }),
        });
        const reglasData = await reglasRes.json().catch(() => ({}));
        if (!reglasRes.ok) {
          showWarning(reglasData.Error || 'No se pudieron consultar las reglas del motor contable');
          return;
        }
        const reglasDoc = reglasMotorDeDocumento(reglasData.registros, tipoDoc);
        if (!documentoMotorDefinido(reglasDoc, tipoDoc)) {
          showWarning(
            `No hay reglas del motor para ${tipoDoc}; no se registró en diario/histdia`
          );
          return;
        }
        const payloadDocumento = construirDocumentoMotorVenta(tipoDoc, reglasDoc, ctxDocumento);
        const motorRes = await fetch(`${servidor}/contabilidadbackend_enviarmotorcontable`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            modulos,
            documento: payloadDocumento,
          }),
        });
        const motorData = await motorRes.json().catch(() => ({}));
        if (!motorRes.ok) {
          showWarning(motorData.Error || 'No se pudo enviar la venta al motor contable');
          return;
        }
        if (motorData.omitido) {
          if (motorData.mensaje) showWarning(motorData.mensaje);
          return;
        }
        if (motorData.ok) {
          if (motorData.descuadrado) {
            showWarning(motorData.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorData.mensaje || 'Motor contable: asiento generado');
          }
        } else if (motorData.mensaje) {
          showWarning(motorData.mensaje);
        }
      } catch {
        showWarning('Error de conexión al enviar la venta al motor contable');
      }
    },
    [modulos, enviarMotorContable, servidor, selectedCompany, usuarioLog, claveLog, showWarning, showSuccess]
  );

  useEffect(() => {
    if (esEspecial || subtotal <= 0) {
      setImpuestosFactura([]);
      return;
    }
    calcularImpuestos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, descuentoGeneralTotal, itemsFactura, esEspecial]);

  useEffect(() => {
    if (tiposDocumentosFiscales.length > 0 && !tipoDocumento) {
      const credito = tiposDocumentosFiscales.find(
        (t) =>
          (t.nombre || '').toLowerCase().includes('credito') ||
          (t.codigo || '').toLowerCase().includes('credito')
      );
      setTipoDocumento(credito ? credito.nombre : tiposDocumentosFiscales[0].nombre);
    }
  }, [tiposDocumentosFiscales, tipoDocumento]);

  useEffect(() => {
    if (clienteSeleccionado?.id) cargarConfigCliente(clienteSeleccionado.id);
    else setConfigCliente(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteSeleccionado?.id]);

  useEffect(() => {
    if (!clienteSeleccionado) {
      setListaPrecioCliente(null);
      return;
    }
    if (listasPrecios.length === 0) return;
    const listaId = configCliente?.lista_precio_id ?? clienteSeleccionado?.lista_precio_id;
    if (listaId) {
      const lista = listasPrecios.find((l) => l.id === listaId);
      setListaPrecioCliente(lista || null);
    } else {
      setListaPrecioCliente(null);
    }
  }, [
    clienteSeleccionado,
    configCliente?.lista_precio_id,
    clienteSeleccionado?.lista_precio_id,
    listasPrecios,
  ]);

  // Con lista asignada al cliente se pasa a precio mayor automáticamente.
  useEffect(() => {
    setTipoPrecio(listaPrecioCliente ? 'mayor' : 'detalle');
  }, [listaPrecioCliente?.id]);

  const prevListaPrecioIdRef = useRef(undefined);
  const prevTipoPrecioRef = useRef('detalle');
  useEffect(() => {
    if (!permisoLeer || !selectedCompany?.db_empresa) return;
    const cambioLista = prevListaPrecioIdRef.current !== listaPrecioCliente?.id;
    const cambioTipo = prevTipoPrecioRef.current !== tipoPrecio;
    if (!cambioLista && !cambioTipo) return;
    prevListaPrecioIdRef.current = listaPrecioCliente?.id;
    prevTipoPrecioRef.current = tipoPrecio;
    if (cambioLista) {
      cargarProductos();
      limpiarFactura();
    } else {
      cargarProductos().then((lista) => {
        if (lista) repreciarItems(lista, setItemsFactura, (p, c) => aplicarReglasDescuento(p, c));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listaPrecioCliente?.id, tipoPrecio, permisoLeer, selectedCompany]);

  // --- Cálculos y ediciones de la factura --------------------------------

  const calcularImpuestos = async () => {
    if (esEspecialRef.current || subtotal <= 0) {
      setImpuestosFactura([]);
      return;
    }
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_calcularimpuestos',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            subtotal: subtotal - descuentoGeneralTotal,
            itemsfactura: itemsFactura,
          }),
        }
      );
      if (esEspecialRef.current) {
        setImpuestosFactura([]);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.impuestos && Array.isArray(data.impuestos)) {
          setImpuestosFactura(
            data.impuestos.map((imp) => ({
              id: imp.id || imp.codigo || Date.now() + Math.random(),
              codigo: imp.codigo || imp.nombre || 'IMP',
              nombre: imp.nombre || imp.descripcion || 'Impuesto',
              porcentaje: parseFloat(imp.porcentaje) || 0,
              monto: parseFloat(imp.monto) || 0,
            }))
          );
        } else {
          setImpuestosFactura([]);
        }
      }
    } catch (e) {
      setImpuestosFactura([]);
    }
  };

  const aplicarReglasDescuento = (producto, cantidad = 1) => {
    let descuentoTotal = 0;
    const precioUnitario = parseFloat(producto.precio) || 0;
    const reglasAplicables = reglasDescuentos.filter((regla) => {
      if (regla.tipoAplicacion === 'producto')
        return reglaDescuentoAplicaAProducto(regla, producto);
      if (
        regla.tipoAplicacion === 'departamento' &&
        producto.departamento &&
        regla.departamento === producto.departamento
      )
        return true;
      return false;
    });
    if (reglasAplicables.length > 0) {
      const regla = reglasAplicables[0];
      if (regla.tipoDescuento === 'porcentaje') {
        const pct = parseFloat(regla.porcentaje) || 0;
        descuentoTotal = precioUnitario * (pct / 100) * cantidad;
      } else if (regla.tipoDescuento === '2x1') {
        const pares = Math.floor(cantidad / 2);
        descuentoTotal = pares * precioUnitario;
      }
    }
    if (listaPrecioCliente !== null) return 0;
    return descuentoTotal;
  };

  const agregarProducto = (producto, cantidad = 1) => {
    if (!permisoModificar) {
      showError('No tiene permiso para modificar la factura');
      return;
    }
    const cant = Math.max(0.01, parseFloat(cantidad) || 1);
    const descuento = aplicarReglasDescuento(producto, cant);
    const subtotalItem = Math.max(
      0,
      cant * (parseFloat(producto.precio) || 0) - descuento
    );
    const nuevoItem = {
      id: Date.now() + Math.random(),
      codigo: producto.codigo,
      descripcion: producto.descripcion,
      tipo: 'producto',
      cantidad: cant,
      precio: parseFloat(producto.precio) || 0,
      descuento,
      descuento_general: 0,
      subtotal: subtotalItem,
      existencia: parseFloat(producto.existencia) || 0,
      departamento: producto.departamento || null,
      impuesto: esEspecial ? 0 : producto.impuesto || null,
      nombreimpuesto: esEspecial ? null : producto.nombreimpuesto || null,
      tipoproducto: 'item',
      localidad: producto.localidad || null,
    };
    const existente = itemsFactura.find(
      (i) => i.codigo === producto.codigo && i.tipo === 'producto'
    );
    if (existente) {
      setItemsFactura((prev) =>
        prev.map((item) => {
          if (item.id !== existente.id) return item;
          const nuevaCant = parseFloat(item.cantidad) + cant;
          const nuevoDesc = aplicarReglasDescuento(producto, nuevaCant);
          return {
            ...item,
            cantidad: nuevaCant,
            descuento: nuevoDesc,
            subtotal: Math.max(0, nuevaCant * item.precio - nuevoDesc),
          };
        })
      );
    } else {
      setItemsFactura((prev) => [...prev, nuevoItem]);
    }
    setDialogoProductoAbierto(false);
    setBusquedaProducto('');
    setCantidadAgregar(1);
    setCantidadesPorProducto((prev) => {
      const { [producto.codigo]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const eliminarItem = (id) => {
    if (!permisoModificar) return;
    setItemsFactura((prev) => prev.filter((item) => item.id !== id));
  };

  const actualizarCantidad = (id, nuevaCantidad) => {
    if (!permisoModificar) return;
    const num = parseFloat(nuevaCantidad);
    if (isNaN(num) || num <= 0) return;
    setItemsFactura((prev) =>
      prev.map((item) => {
        if (item.id !== id) return item;
        const producto = productos.find((p) => p.codigo === item.codigo) || item;
        const desc = aplicarReglasDescuento(producto, num);
        const sub = Math.max(0, num * (parseFloat(item.precio) || 0) - desc);
        return { ...item, cantidad: num, descuento: desc, subtotal: sub };
      })
    );
  };

  const validarClienteYLimites = () => {
    if (!clienteSeleccionado) {
      showError('Seleccione un cliente');
      return false;
    }
    if (!configCliente) return true;
    const limCredito = parseFloat(configCliente.limcredito) || 0;
    const limCompra = parseFloat(configCliente.limcompra) || 0;
    if (tipoFactura === 'credito' && limCredito > 0 && total > limCredito) {
      showError(
        `El total (${total.toFixed(2)}) supera el límite de crédito del cliente (${limCredito.toFixed(2)})`
      );
      return false;
    }
    if (limCompra > 0 && total > limCompra) {
      showError(
        `El total (${total.toFixed(2)}) supera el límite de compra del cliente (${limCompra.toFixed(2)})`
      );
      return false;
    }
    return true;
  };

  const descargarXmlECF = (xmlTexto, filename) => {
    descargarArchivoECF(new Blob([xmlTexto], { type: 'text/xml' }), filename);
  };

  const descargarArchivoECF = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'factura_ecf.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const guardarFactura = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para crear facturas a crédito');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (itemsFactura.length === 0) {
      showError('Agregue al menos un ítem');
      return;
    }
    if (!tipoDocumento) {
      showError('Seleccione el tipo de documento');
      return;
    }
    if (!validarClienteYLimites()) return;

    const fechaMotor = hoyLocal();
    const enviarMotor = debeEnviarAlMotor(modulos, enviarMotorContable);
    const tipoDocMotor = TIPO_DOCUMENTO_MOTOR_VENTAS_CREDITO;
    const ingresosMotor = redondearDinero2(subtotalAntesDescuentos);
    const itbisMotor = redondearDinero2(esEspecial ? 0 : totalImpuestos);
    const descuentosMotor = redondearDinero2(totalDescuentos);
    const totalMotor = redondearDinero2(total);
    const montosMotor = enviarMotor
      ? {
          ingreso: ingresosMotor,
          ingresos: ingresosMotor,
          itbis: itbisMotor,
          descuentos: descuentosMotor,
          cuentasxcobrar: totalMotor,
        }
      : null;
    const payloadMotor = enviarMotor
      ? {
          modulos,
          tipodocumento: tipoDocMotor,
          documento_diario: DOCUMENTO_DIARIO_VENTAS_CREDITO,
          lineas_motor: [],
          montos: montosMotor,
        }
      : {};

    setGuardando(true);
    try {
      const itemsConDescuentoGeneral = itemsFactura.map((item) => {
        const descGen = getDescuentoGeneralPorItem(item);
        return {
          ...item,
          descuento_general: descGen,
          subtotal: Math.max(0, (parseFloat(item.subtotal) || 0) - descGen),
          impuesto: esEspecial ? 0 : parseFloat(item.impuesto) || 0,
        };
      });
      const subtotalNeto = subtotal - descuentoGeneralTotal;

      // RNC/Cédula del comprador: se resuelve desde maestcta (clienteSeleccionado
      // o configCliente, que vienen de SELECT * FROM maestcta). El backend lo
      // exige para comprobantes distintos de Consumo (E32).
      const rncComprador =
        clienteSeleccionado?.rnc ||
        configCliente?.rnc ||
        '';
      const razonSocialComprador =
        clienteSeleccionado?.descrip ||
        clienteSeleccionado?.descripcion ||
        configCliente?.descrip ||
        '';

      // Guardado de la factura mediante el helper del contexto (mismo endpoint y
      // reglas que FacturaPOS). Los datos del cliente se conservan tal cual.
      const body = {
        id_empresa: selectedCompany,
        usuario: usuarioLog || '',
        password: claveLog || '',
        anomesfacturacion: "productosnoserializados",
        localidad: localStorage.getItem('sucursal') || '',
        ...payloadMotor,
        factura: {
          cliente: String(clienteSeleccionado.id),
          rnc: rncComprador,
          razonsocial: razonSocialComprador,
          items: itemsConDescuentoGeneral,
          impuestos: esEspecial ? [] : impuestosFactura,
          subtotal: subtotalNeto,
          total_descuentos: totalDescuentos,
          total_impuestos: esEspecial ? 0 : totalImpuestos,
          total,
          ncf: '',
          tipo_documento: tipoDocumento,
          tipo_factura: TIPO_FACTURA_CREDITO,
          fecha: fechaMotor,
          usuario: usuarioLog || '',
          observaciones: observaciones.trim(),
          pago: null,
          cotizacion_id: cotizacionSeleccionada || null,
          orden_pedido_id: ordenPedidoSeleccionada || null,
          comprobanteelectronico: comprobanteelectronico === 'S' ? 'S' : 'N',
          modulosasignados: modulos,
          ...(enviarMotor
            ? {
                tipodocumento: tipoDocMotor,
                documento_diario: DOCUMENTO_DIARIO_VENTAS_CREDITO,
              }
            : {}),
        },
      };

      const { ok, data, error } = await guardarFacturaBackend(servidor, body);
      if (!ok) {
        showError(error || 'Error al guardar la factura');
        return;
      }

      showSuccess(`Factura ${data.documento}-${data.numero} guardada`);

      let fechaFirmaEcf = '';
      let codigoSeguridadEcf = '';
      let montoTotalEcf = null;
      let facturaRevertida = false;

      try {
        if (comprobanteelectronico === 'S') {
          const ecfPayload = {
            comprobanteelectronico,
            rnc: rncComprador,
            razonsocial: razonSocialComprador,
            comprobante: data.comprobantefiscal || '',
            tipocomprobante: tipoDocumento,
            usuario: usuarioLog || '',
            password: claveLog || '',
            pago: null,
            items: itemsConDescuentoGeneral.map((it) => ({
              item: it.descripcion,
              cantidad: parseFloat(it.cantidad) || 0,
              valor: parseFloat(it.precio) || 0,
              descuento: (parseFloat(it.descuento) || 0) + (parseFloat(it.descuento_general) || 0),
              tasaitbis: esEspecial ? 0 : parseFloat(it.impuesto) || 0,
            })),
          };
          const ecf = await enviarPayloadFacturaElectronica(servidor, ecfPayload);
          if (ecf.ok) {
            descargarArchivoECF(ecf.blob, ecf.filename);
          } else {
            showError(ecf.error || 'No se pudo generar el comprobante electrónico');
          }

          const emision = await emitirEcfDgii(servidor, {
            ...ecfPayload,
            rncemisor: rnc_empresa || '',
            razonsocialemisor: typeof nombreNegocio === 'string' ? nombreNegocio : '',
            direccionemisor: direccion_empresa || '',
          });
          if (emision.ok) {
            descargarXmlECF(emision.xml, `${emision.encf || 'ecf'}.xml`);
            if (emision.xml_rfce) {
              descargarXmlECF(emision.xml_rfce, `${emision.encf || 'ecf'}-resumen.xml`);
            }
            fechaFirmaEcf = emision.fecha_firma || '';
            codigoSeguridadEcf = emision.codigo_seguridad || '';
            montoTotalEcf = emision.monto_total ?? null;
            const etiquetas = {
              aceptado: 'aceptado por la DGII',
              aceptado_condicional: 'aceptado condicional por la DGII',
              en_proceso: `en proceso en la DGII${emision.track_id ? ` (TrackId ${emision.track_id})` : ''}`,
              rechazado: 'RECHAZADO por la DGII',
              pendiente_envio: 'firmado; DGII inaccesible, se reenviará',
              error: 'firmado; el envío a la DGII falló',
            };
            const detalleDgii = (emision.respuesta_dgii?.mensajes || [])
              .map((m) => `${m.valor || m.mensaje || ''}${m.codigo ? ` (${m.codigo})` : ''}`)
              .filter((t) => t.trim())
              .join(' · ');
            const mensaje =
              `e-CF ${emision.encf} ${etiquetas[emision.estado] || emision.estado}` +
              ` · código ${emision.codigo_seguridad}` +
              (detalleDgii ? ` — DGII: ${detalleDgii}` : '');
            if (emision.estado === 'rechazado') {
              showError(mensaje);
              const revertBody = {
                id_empresa: selectedCompany,
                usuario: usuarioLog || '',
                password: claveLog || '',
                nfact: data.numero,
                tipo_documento: tipoDocumento,
                anomesfacturacion: 'productosnoserializados',
                motoraguardar: 'N',
              };
              const rev = await revertirFacturaRechazada(servidor, revertBody);
              if (rev.ok) {
                showError(`Factura ${data.documento}-${data.numero} revertida automáticamente (e-CF rechazado)`);
              } else {
                showError(rev.error || 'No se pudo revertir la factura rechazada');
              }
              facturaRevertida = true;
              limpiarFactura();
            } else {
              showSuccess(mensaje);
            }
          } else {
            showError(emision.error || 'No se pudo emitir el e-CF');
          }
        }
      } catch (eEcf) {
        showError(eEcf?.message || 'Error al emitir el comprobante electrónico');
      }

      if (enviarMotor && !facturaRevertida) {
        const ctxMotor = {
          valorTotal: totalMotor,
          formaPago: '',
          lineas: [],
          nfactura: String(data.numero || ''),
          documento: DOCUMENTO_DIARIO_VENTAS_CREDITO,
          fecha: fechaMotor,
          tasa: 1,
          usuario: usuarioLog || '',
          concepto: `${tipoDocMotor} ${DOCUMENTO_DIARIO_VENTAS_CREDITO}-${data.numero}`,
          montos: montosMotor,
        };
        const motorSave = data.motor;
        if (motorSave?.registrado) {
          if (motorSave.descuadrado) {
            showWarning(motorSave.mensaje || 'Motor contable: asiento guardado descuadrado');
          } else {
            showSuccess(motorSave.mensaje || 'Motor contable: asiento registrado en diario e histdia');
          }
        } else if (motorSave?.omitido) {
          if (motorSave.mensaje) showWarning(motorSave.mensaje);
        } else {
          if (motorSave?.mensaje) showWarning(motorSave.mensaje);
          await enviarAlMotorContableVenta(tipoDocMotor, ctxMotor);
        }
      }

      if (facturaRevertida) {
        return;
      }

      const itemsVista = itemsConDescuentoGeneral.map((i) => ({
        ...i,
        descuento_general: getDescuentoGeneralPorItem(i),
      }));
      const tipoDocumentoResuelto =
        docFiscalSeleccionado?.nombre || tipoDocumento;
      const configuracionImpresion = await obtenerConfiguracionParaTipo('FACTURA CREDITO');

      setDatosVistaFactura({
        items: itemsVista,
        impuestos: esEspecial ? [] : impuestosFactura,
        subtotal: subtotalNeto,
        totalDescuentos,
        totalImpuestos: esEspecial ? 0 : totalImpuestos,
        total,
        documento: data.documento,
        numero: String(data.numero || ''),
        fecha: fechaMotor,
        ncf: data.comprobantefiscal || '',
        tipoDocumento: tipoDocumentoResuelto,
        observaciones,
        empresa: selectedCompany?.empresa || selectedCompany?.nombre || selectedCompany,
        id_empresa: selectedCompany,
        tipo_factura: TIPO_FACTURA_CREDITO,
        cliente_nombre:
          clienteSeleccionado?.descrip || clienteSeleccionado?.descripcion || '',
        comprobanteelectronico,
        codigocomprobante: docFiscalSeleccionado?.codigo ?? '',
        fechavencimiento: docFiscalSeleccionado?.fecha_valida ?? docFiscalSeleccionado?.valido ?? '',
        rnc: rncComprador,
        fecha_firma: fechaFirmaEcf,
        codigo_seguridad: codigoSeguridadEcf,
        monto_total_ecf: montoTotalEcf,
        configuracion: configuracionImpresion || undefined,
      });
      setVistaFacturaAbierta(true);
      limpiarFactura();
    } catch (e) {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const manejarGuardarFacturaClick = () => {
    if (!permisoModificar) {
      showError('No tiene permiso para crear facturas a crédito');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (itemsFactura.length === 0) {
      showError('Agregue al menos un ítem');
      return;
    }
    if (!tipoDocumento) {
      showError('Seleccione el tipo de documento');
      return;
    }
    if (!validarClienteYLimites()) return;
    guardarFactura();
  };

  const limpiarFactura = () => {
    setItemsFactura([]);
    setImpuestosFactura([]);
    setObservaciones('');
    setCotizacionSeleccionada(null);
    setOrdenPedidoSeleccionada(null);
  };

  // --- Cotizaciones ------------------------------------------------------

  const cargarCotizaciones = async (busqueda = '') => {
    setCargandoCotizaciones(true);
    try {
      const response = await fetch(
        servidor + '/api/facturabackend_listarcotizaciones',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            busqueda: busqueda,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCotizaciones(data.cotizaciones || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar cotizaciones');
      }
    } catch (error) {
      showError('Error de conexión al cargar cotizaciones');
    } finally {
      setCargandoCotizaciones(false);
    }
  };

  const abrirDialogoCotizaciones = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para importar cotizaciones a la factura');
      return;
    }
    setDialogoCotizaciones(true);
    setBusquedaCotizacion('');
    await cargarCotizaciones('');
  };

  const importarCotizacion = async (cotizacionId) => {
    try {
      setCotizacionSeleccionada(cotizacionId);
      const response = await fetch(
        servidor + '/api/facturabackend_obtenercotizacion',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id_empresa: selectedCompany,
            usuario: usuarioLog || '',
            password: claveLog || '',
            cotizacion_id: cotizacionId,
          }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        const cotizacion = data.cotizacion;
        const items = data.items || [];
        if (items.length === 0) {
          showError('La cotización seleccionada no tiene items');
          return;
        }
        const nuevosItems = items.map((item, index) => ({
          id: Date.now() + index,
          codigo: item.codigo,
          descripcion: item.descripcion,
          cantidad: parseFloat(item.cantidad) || 1,
          precio: parseFloat(item.precio) || 0,
          descuento: parseFloat(item.descuento) || 0,
          descuento_general: 0,
          subtotal: parseFloat(item.subtotal) || 0,
          tipo: item.tipo_item || 'producto',
          existencia: 0,
          departamento: null,
          impuesto: esEspecial ? 0 : item.impuesto || null,
          nombreimpuesto: null,
          tipoproducto: 'item',
        }));
        setItemsFactura(nuevosItems);
        if (cotizacion.observaciones) {
          setObservaciones(cotizacion.observaciones);
        }
        showSuccess(
          `Cotización "${cotizacion.numero} - ${cotizacion.nombre}" importada exitosamente`
        );
        setDialogoCotizaciones(false);
        setBusquedaCotizacion('');
        try {
          await fetch(servidor + '/api/facturabackend_marcarcotizacionimportada', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id_empresa: selectedCompany,
              usuario: usuarioLog || '',
              password: claveLog || '',
              cotizacion_id: cotizacionId,
            }),
          });
        } catch (_) {
          // No hace falta interrumpir el flujo si el marcado falla.
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al importar cotización');
      }
    } catch (error) {
      showError('Error de conexión al importar cotización');
    }
  };

  const handleBuscarCotizacion = async (busqueda) => {
    setBusquedaCotizacion(busqueda);
    await cargarCotizaciones(busqueda);
  };

  // --- Órdenes de pedido -------------------------------------------------

  const cargarOrdenesPedido = async (busqueda = '') => {
    setCargandoOrdenesPedido(true);
    try {
      const response = await fetch(`${servidor}/api/ordenesdepedidos_listar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda,
          solo_abiertas: 'S',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setOrdenesPedido(data.ordenes || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar órdenes de pedido');
      }
    } catch {
      showError('Error de conexión al cargar órdenes de pedido');
    } finally {
      setCargandoOrdenesPedido(false);
    }
  };

  const abrirDialogoOrdenesPedido = async () => {
    if (!permisoModificar) {
      showError('No tiene permiso para importar órdenes de pedido');
      return;
    }
    setDialogoOrdenesPedido(true);
    setBusquedaOrdenPedido('');
    await cargarOrdenesPedido('');
  };

  const importarOrdenPedido = async (ordenId) => {
    try {
      setOrdenPedidoSeleccionada(ordenId);
      const response = await fetch(`${servidor}/api/ordenesdepedidos_obtener`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          orden_id: ordenId,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al importar la orden');
        return;
      }
      const data = await response.json();
      const orden = data.orden || {};
      const items = data.items || [];
      if (items.length === 0) {
        showError('La orden seleccionada no tiene ítems');
        return;
      }

      const nuevosItems = items.map((item, index) => ({
        id: Date.now() + index,
        codigo: item.codigo,
        descripcion: item.descripcion,
        cantidad: parseFloat(item.cantidad) || 1,
        precio: parseFloat(item.precio) || 0,
        descuento: parseFloat(item.descuento) || 0,
        descuento_general: 0,
        subtotal: parseFloat(item.subtotal) || 0,
        tipo: item.tipo_item || 'producto',
        existencia: 0,
        departamento: null,
        impuesto: esEspecial ? 0 : item.impuesto || null,
        nombreimpuesto: null,
        tipoproducto: 'item',
      }));
      setItemsFactura(nuevosItems);

      if (orden.observaciones) setObservaciones(orden.observaciones);

      const tipoOrd = String(orden.tipo_factura || '').toUpperCase();
      if (tipoOrd.includes('CREDITO') || tipoOrd.includes('CONTADO')) {
        setTipoFactura('credito');
      }

      if (orden.cliente_id) {
        const encontrado = clientes.find(
          (c) => String(c.id) === String(orden.cliente_id)
        );
        if (encontrado) {
          setClienteSeleccionado(encontrado);
          setBusquedaCliente(
            `${encontrado.id || ''} - ${encontrado.descrip || encontrado.descripcion || ''}`
          );
          await cargarConfigCliente(encontrado.id);
        }
      }

      showSuccess(`Orden de pedido #${orden.numero} importada`);
      setDialogoOrdenesPedido(false);
      setBusquedaOrdenPedido('');
    } catch {
      showError('Error de conexión al importar la orden');
    }
  };

  // --- Filtros locales ---------------------------------------------------

  const productosFiltrados = useMemo(() => {
    if (!busquedaProducto.trim()) return productos.slice(0, 50);
    const b = busquedaProducto.toLowerCase();
    return productos
      .filter(
        (p) =>
          String(p.codigo || '').toLowerCase().includes(b) ||
          String(p.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 50);
  }, [productos, busquedaProducto]);

  const clientesFiltrados = useMemo(() => {
    if (!busquedaCliente.trim()) return clientes.slice(0, 100);
    const b = busquedaCliente.toLowerCase();
    return clientes
      .filter(
        (c) =>
          String(c.id || '').includes(b) ||
          String(c.descrip || c.descripcion || '').toLowerCase().includes(b)
      )
      .slice(0, 100);
  }, [clientes, busquedaCliente]);

  // --- Acceso denegado ---------------------------------------------------

  if (!permisoLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader
          title="Factura a crédito"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Emisión de comprobantes` : 'Emisión de comprobantes'
          }
          cta={
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setDialogoConsultaProducto(true)}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Consultar producto
            </Button>
          }
        />
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              ...cardShellSx,
              maxWidth: 560,
              width: '100%',
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «facturacredito» en roles para crear facturas a crédito.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
        <ConsultaProducto
          open={dialogoConsultaProducto}
          onClose={() => setDialogoConsultaProducto(false)}
        />
      </Box>
    );
  }

  // --- Render principal --------------------------------------------------

  const tituloDoc = 'Factura a crédito';

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title={tituloDoc}
        subtitle={
          nombreNegocio ? `${nombreNegocio} · Emisión de comprobantes` : 'Emisión de comprobantes'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        {!permisoModificar && (
          <Alert
            severity="warning"
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.warningSoft,
            }}
          >
            Solo lectura: tiene permiso de consulta pero no puede emitir ni modificar facturas.
          </Alert>
        )}

        {/* Bloque: datos generales / cliente */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Datos del documento
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              startIcon={<InfoOutlinedIcon />}
              onClick={() => setDialogoConsultaProducto(true)}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                mr: 1,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Consultar producto
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={abrirDialogoCotizaciones}
              disabled={!permisoModificar}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                mr: 1,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Importar cotización
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={abrirDialogoOrdenesPedido}
              disabled={!permisoModificar}
              size="small"
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Importar orden
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5} alignItems="flex-start">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  options={clientesFiltrados}
                  getOptionLabel={(opt) =>
                    `${opt.id || ''} - ${opt.descrip || opt.descripcion || ''}`
                  }
                  value={clienteSeleccionado}
                  onChange={(_, val) => setClienteSeleccionado(val)}
                  inputValue={busquedaCliente}
                  onInputChange={(_, val) => setBusquedaCliente(val)}
                  disabled={!permisoModificar}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      required
                      placeholder="Buscar cliente…"
                      InputLabelProps={{ shrink: true }}
                      sx={fpInputSx}
                    />
                  )}
                />
                {configCliente && (
                  <Alert
                    severity="info"
                    sx={{
                      mt: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${FP.color.border}`,
                      bgcolor: FP.color.infoSoft,
                    }}
                  >
                    Límite crédito: ${fmtMoney(configCliente.limcredito)} · Límite compra: $
                    {fmtMoney(configCliente.limcompra)}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={observaciones}
                  label="Observaciones"
                  onChange={(e) => setObservaciones(e.target.value)}
                  placeholder="Observaciones de la factura…"
                  disabled={!permisoModificar}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { paddingTop: '3.8em' } }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="tipo-doc-label" shrink>
                    Tipo documento
                  </InputLabel>
                  <Select
                    labelId="tipo-doc-label"
                    label="Tipo documento"
                    value={tipoDocumento}
                    onChange={(e) => cambiarDocumentoFiscal(e.target.value)}
                    disabled={!permisoModificar}
                    displayEmpty
                  >
                    {tiposDocumentosFiscales.map((t) => (
                      <MenuItem key={t.id || t.nombre} value={t.nombre}>
                        {t.nombre}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Tipo de factura"
                  value="Crédito"
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Sucursal"
                  value={sucursalActual || 'No definida'}
                  InputProps={{ readOnly: true }}
                  helperText="Sucursal del usuario actual"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  select
                  fullWidth
                  label="Tipo de precio"
                  value={tipoPrecio}
                  onChange={(e) => setTipoPrecio(e.target.value)}
                  helperText={
                    listaPrecioCliente
                      ? `Lista del cliente: ${listaPrecioCliente.codigo} - ${listaPrecioCliente.descripcion || ''}`
                      : 'Mayor usa el campo Mayor de cada producto'
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                >
                  <MenuItem value="detalle">Detalle</MenuItem>
                  <MenuItem value="mayor">Mayor (precio x mayor del producto)</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Bloque: ítems y totales */}
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ShoppingCartIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Items de la factura
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogoProductoAbierto(true)}
              disabled={cargando || !permisoModificar}
              size="small"
              sx={{
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                borderRadius: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Agregar producto
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
                maxHeight: 360,
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell sx={cellHeadSx}>Código</TableCell>
                    <TableCell sx={cellHeadSx}>Descripción</TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Cant.
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Precio
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Descuento
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Subtotal
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                  {itemsFactura.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                          No hay items en la factura. Use «Agregar producto».
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemsFactura.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Chip
                            label={row.codigo}
                            size="small"
                            sx={{
                              bgcolor: FP.color.accentBlueSoft,
                              color: FP.color.accentBlue,
                              fontWeight: 700,
                              fontSize: '0.74rem',
                            }}
                          />
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 500, color: FP.color.textPrimary, fontSize: '0.86rem' }}
                          title={row.descripcion}
                        >
                          {row.descripcion}
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            type="number"
                            size="small"
                            inputProps={{ min: 0.01, step: 0.1, style: { textAlign: 'center' } }}
                            value={row.cantidad}
                            disabled={!permisoModificar}
                            onChange={(e) => actualizarCantidad(row.id, e.target.value)}
                            sx={{ width: 90, ...fpInputSx }}
                          />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 700,
                            color: FP.color.textPrimary,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '0.86rem',
                          }}
                        >
                          ${fmtMoney(row.precio)}
                        </TableCell>
                        <TableCell align="right">
                          {parseFloat(row.descuento || 0) > 0 ? (
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: FP.color.danger,
                                fontVariantNumeric: 'tabular-nums',
                                fontSize: '0.86rem',
                              }}
                            >
                              -${fmtMoney(row.descuento || 0)}
                            </Typography>
                          ) : (
                            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.86rem' }}>
                              $0.00
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 800,
                            color: FP.color.navy,
                            fontVariantNumeric: 'tabular-nums',
                            fontSize: '0.92rem',
                          }}
                        >
                          ${fmtMoney(row.subtotal)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => eliminarItem(row.id)}
                            disabled={!permisoModificar}
                            sx={{
                              color: FP.color.danger,
                              '&:hover': { bgcolor: FP.color.dangerSoft },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Totales */}
            <Box
              sx={{
                mt: 2,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  Subtotal:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(subtotalAntesDescuentos)}
                </Typography>
              </Box>
              {totalDescuentos > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography
                    sx={{ fontWeight: 600, color: FP.color.textSecondary, pl: 1 }}
                  >
                    Total descuentos:
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      color: FP.color.danger,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    -${fmtMoney(totalDescuentos)}
                  </Typography>
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  pb: 1,
                  borderBottom: `1px dashed ${FP.color.border}`,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                  Subtotal después de descuentos:
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: FP.color.textPrimary,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(subtotal - descuentoGeneralTotal)}
                </Typography>
              </Box>
              {!esEspecial && impuestosFactura.length > 0 ? (
                <>
                  {impuestosFactura.map((imp) => (
                    <Box
                      key={imp.id}
                      sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}
                    >
                      <Typography
                        sx={{ fontWeight: 500, color: FP.color.textSecondary, pl: 2 }}
                      >
                        {imp.nombre}
                        {imp.porcentaje ? ` (${parseFloat(imp.porcentaje).toFixed(2)}%)` : ''}:
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        ${fmtMoney(imp.monto || 0)}
                      </Typography>
                    </Box>
                  ))}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      mb: 1.5,
                      mt: 1,
                      pt: 1,
                      borderTop: `1px solid ${FP.color.border}`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                      Total impuestos:
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        color: FP.color.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      ${fmtMoney(totalImpuestos)}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textSecondary }}>
                    Impuestos:
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textMuted }}>$0.00</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1.5, borderColor: FP.color.border }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderRadius: 1.6,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>Total:</Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: '1.5rem',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  ${fmtMoney(total)}
                </Typography>
              </Box>
            </Box>

            {/* Acciones */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2.5 }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={limpiarFactura}
                disabled={!permisoModificar}
                fullWidth
                sx={{
                  height: 50,
                  flex: 1,
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                }}
              >
                Limpiar
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={manejarGuardarFacturaClick}
                fullWidth
                disabled={
                  guardando ||
                  itemsFactura.length === 0 ||
                  !clienteSeleccionado ||
                  !permisoModificar ||
                  !usuarioLog
                }
                sx={{
                  height: 50,
                  flex: 1,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 800,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: FP.color.surfaceAlt, color: FP.color.textMuted },
                }}
              >
                {guardando ? 'Guardando…' : 'Guardar factura'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Diálogo agregar producto */}
      <Dialog
        open={dialogoProductoAbierto}
        onClose={() => {
          setDialogoProductoAbierto(false);
          setCantidadesPorProducto({});
          setBusquedaProducto('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography
            component="span"
            sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}
          >
            Agregar producto
          </Typography>
          <IconButton
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: FP.color.surface, p: 3, pt: 3.5 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              mb: 2.5,
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Autocomplete
              fullWidth
              freeSolo
              autoHighlight
              openOnFocus
              clearOnBlur={false}
              selectOnFocus
              handleHomeEndKeys
              options={productos}
              filterOptions={(opts, { inputValue }) => {
                const b = String(inputValue || '').trim().toLowerCase();
                if (!b) return opts.slice(0, 50);
                return opts
                  .filter(
                    (p) =>
                      String(p.codigo || '').toLowerCase().includes(b) ||
                      String(p.descripcion || '').toLowerCase().includes(b)
                  )
                  .slice(0, 50);
              }}
              getOptionLabel={(opt) =>
                typeof opt === 'string'
                  ? opt
                  : `${opt.codigo || ''} - ${opt.descripcion || ''}`
              }
              isOptionEqualToValue={(opt, val) => opt?.codigo === val?.codigo}
              inputValue={busquedaProducto}
              onInputChange={(_, val, reason) => {
                if (reason === 'reset') return;
                setBusquedaProducto(val);
              }}
              onChange={(_, val) => {
                if (val && typeof val !== 'string') {
                  const cant = getCantidadProducto(val.codigo) || cantidadAgregar;
                  agregarProducto(val, cant);
                }
              }}
              noOptionsText="No hay productos que coincidan"
              renderOption={(props, opt) => (
                <Box
                  component="li"
                  {...props}
                  key={opt.codigo}
                  sx={{
                    display: 'flex !important',
                    alignItems: 'center !important',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    <Chip
                      label={opt.codigo}
                      size="small"
                      sx={{
                        bgcolor: FP.color.accentBlueSoft,
                        color: FP.color.accentBlue,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.86rem',
                        color: FP.color.textPrimary,
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={opt.descripcion}
                    >
                      {opt.descripcion}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      color: FP.color.textPrimary,
                      fontWeight: 700,
                      fontVariantNumeric: 'tabular-nums',
                      flexShrink: 0,
                    }}
                  >
                    ${fmtMoney(opt.precio || 0)}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  label="Buscar producto"
                  placeholder="Por código o descripción…"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <SearchIcon
                            sx={{ color: FP.color.textMuted }}
                            fontSize="small"
                          />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <TextField
              type="number"
              label="Cantidad por defecto"
              value={cantidadAgregar}
              onChange={(e) =>
                setCantidadAgregar(Math.max(0.01, parseFloat(e.target.value) || 1))
              }
              inputProps={{
                min: 0.01,
                step: 0.01,
                style: { textAlign: 'center' },
              }}
              InputLabelProps={{ shrink: true }}
              sx={{ width: { xs: '100%', sm: 170 }, flexShrink: 0, ...fpInputSx }}
            />
          </Box>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: `1px solid ${FP.color.border}`,
              borderRadius: 2,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <Table size="small" stickyHeader>
              <TableBody>
                <TableRow
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    '& td': {
                      border: 'none',
                      borderBottom: `2px solid ${FP.color.border}`,
                      bgcolor: FP.color.surfaceAlt,
                    },
                  }}
                >
                  <TableCell sx={{ ...cellHeadSx, width: 110 }}>Código</TableCell>
                  <TableCell sx={cellHeadSx}>Descripción</TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'right', width: 110 }} align="right">
                    Precio
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, textAlign: 'center', width: 100 }} align="center">
                    Cant.
                  </TableCell>
                  <TableCell sx={{ ...cellHeadSx, width: 110 }} />
                </TableRow>
                {productosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem' }}>
                        No hay productos o no coincide la búsqueda.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  productosFiltrados.map((p) => (
                    <TableRow key={p.codigo} hover>
                      <TableCell sx={{ fontSize: '0.84rem', color: FP.color.textPrimary }}>
                        {p.codigo}
                      </TableCell>
                      <TableCell
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          py: 1.2,
                          fontSize: '0.85rem',
                          color: FP.color.textPrimary,
                        }}
                        title={p.descripcion}
                      >
                        {p.descripcion}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.86rem',
                          color: FP.color.textPrimary,
                        }}
                      >
                        ${fmtMoney(p.precio || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          type="number"
                          size="small"
                          value={getCantidadProducto(p.codigo)}
                          onChange={(e) => setCantidadProducto(p.codigo, e.target.value)}
                          inputProps={{
                            min: 0.01,
                            step: 0.01,
                            style: { textAlign: 'center' },
                          }}
                          sx={{ width: 90, ...fpInputSx }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => agregarProducto(p, getCantidadProducto(p.codigo))}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            borderRadius: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Agregar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogoProductoAbierto(false);
              setCantidadesPorProducto({});
              setBusquedaProducto('');
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo importar cotización */}
      <Dialog
        open={dialogoCotizaciones}
        onClose={() => {
          setDialogoCotizaciones(false);
          setBusquedaCotizacion('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              component="span"
              sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}
            >
              Importar cotización
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: FP.color.textSecondary, fontSize: '0.84rem', display: 'block' }}
            >
              Busque por número o nombre de la cotización
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setDialogoCotizaciones(false);
              setBusquedaCotizacion('');
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: FP.color.surface, p: 3 }}>
          <TextField
            fullWidth
            placeholder="Buscar por número o nombre…"
            value={busquedaCotizacion}
            onChange={(e) => handleBuscarCotizacion(e.target.value)}
            sx={{ mb: 2, ...fpInputSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DescriptionIcon sx={{ color: FP.color.textMuted }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          {cargandoCotizaciones ? (
            <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
              Cargando cotizaciones…
            </Typography>
          ) : cotizaciones.length === 0 ? (
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: `1px solid ${FP.color.border}`,
                bgcolor: FP.color.infoSoft,
              }}
            >
              {busquedaCotizacion
                ? 'No se encontraron cotizaciones con ese criterio de búsqueda'
                : 'No hay cotizaciones disponibles. Realice una búsqueda para encontrar cotizaciones.'}
            </Alert>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                maxHeight: 420,
                overflow: 'auto',
              }}
            >
              <Table size="small" stickyHeader>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell sx={cellHeadSx}>Número</TableCell>
                    <TableCell sx={cellHeadSx}>Nombre</TableCell>
                    <TableCell sx={cellHeadSx}>Cliente</TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Fecha
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'right' }} align="right">
                      Total
                    </TableCell>
                    <TableCell sx={{ ...cellHeadSx, textAlign: 'center' }} align="center">
                      Acción
                    </TableCell>
                  </TableRow>
                  {cotizaciones.map((cotizacion) => (
                    <TableRow key={cotizacion.id} hover>
                      <TableCell
                        sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.85rem' }}
                      >
                        {cotizacion.numero}
                      </TableCell>
                      <TableCell sx={{ color: FP.color.textPrimary, fontSize: '0.85rem' }}>
                        {cotizacion.nombre}
                      </TableCell>
                      <TableCell sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                        {cotizacion.cliente || '-'}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ color: FP.color.textSecondary, fontSize: '0.85rem' }}
                      >
                        {cotizacion.fecha
                          ? new Date(cotizacion.fecha).toLocaleDateString('es-DO')
                          : '-'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 700,
                          color: FP.color.textPrimary,
                          fontVariantNumeric: 'tabular-nums',
                          fontSize: '0.86rem',
                        }}
                      >
                        ${fmtMoney(cotizacion.total || 0)}
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<FileUploadIcon />}
                          onClick={() => importarCotizacion(cotizacion.id)}
                          sx={{
                            bgcolor: FP.color.navy,
                            color: '#FFFFFF',
                            borderRadius: 1.2,
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                          }}
                        >
                          Importar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogoCotizaciones(false);
              setBusquedaCotizacion('');
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo importar orden de pedido */}
      <Dialog
        open={dialogoOrdenesPedido}
        onClose={() => {
          setDialogoOrdenesPedido(false);
          setBusquedaOrdenPedido('');
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            maxHeight: '85vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: FP.color.textPrimary }}>
            Importar orden de pedido
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: FP.color.textSecondary, mt: 0.3 }}>
            Solo órdenes activas (no facturadas)
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Buscar"
            value={busquedaOrdenPedido}
            onChange={(e) => {
              setBusquedaOrdenPedido(e.target.value);
              cargarOrdenesPedido(e.target.value);
            }}
            placeholder="Número, cliente o vendedor…"
            InputLabelProps={{ shrink: true }}
            sx={{ ...fpInputSx, mb: 2, mt: 1 }}
          />
          {cargandoOrdenesPedido ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 420,
                border: `1px solid ${FP.color.border}`,
                borderRadius: 2,
                overflow: 'auto',
              }}
            >
              <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 720 }}>
                <colgroup>
                  {['12%', '12%', '28%', '16%', '14%', '18%'].map((w, i) => (
                    <col key={i} style={{ width: w }} />
                  ))}
                </colgroup>
                <TableBody>
                  <TableRow
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      '& td': {
                        border: 'none',
                        borderBottom: `2px solid ${FP.color.border}`,
                        bgcolor: FP.color.surfaceAlt,
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Número
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        Fecha
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Cliente
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                        }}
                      >
                        Vendedor
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                          textAlign: 'right',
                          width: '100%',
                        }}
                      >
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        component="span"
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: FP.color.textSecondary,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          display: 'block',
                          mb: 0.5,
                          textAlign: 'center',
                          width: '100%',
                        }}
                      >
                        Acciones
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {(ordenesPedido || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4, color: FP.color.textMuted }}>
                        No hay órdenes disponibles
                      </TableCell>
                    </TableRow>
                  ) : (
                    ordenesPedido.map((orden) => (
                      <TableRow key={orden.id} hover>
                        <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                          #{orden.numero}
                        </TableCell>
                        <TableCell align="center">{String(orden.fecha || '').slice(0, 10)}</TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={orden.cliente || ''}
                        >
                          {orden.cliente}
                        </TableCell>
                        <TableCell
                          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={orden.vendedor || ''}
                        >
                          {orden.vendedor || '—'}
                        </TableCell>
                        <TableCell align="right">
                          ${(parseFloat(orden.total) || 0).toLocaleString('es-DO', {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<FileUploadIcon />}
                            onClick={() => importarOrdenPedido(orden.id)}
                            sx={{
                              bgcolor: FP.color.navy,
                              color: '#FFF',
                              borderRadius: 1.2,
                              textTransform: 'none',
                              fontWeight: 700,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                            }}
                          >
                            Importar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#FAFBFE', borderTop: `1px solid ${FP.color.border}`, px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDialogoOrdenesPedido(false);
              setBusquedaOrdenPedido('');
            }}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <ConsultaProducto
        open={dialogoConsultaProducto}
        onClose={() => setDialogoConsultaProducto(false)}
      />

      <VistaFactura
        open={vistaFacturaAbierta}
        onClose={() => setVistaFacturaAbierta(false)}
        facturaData={datosVistaFactura}
      />
    </Box>
  );
};

export default FacturaCredito;
